"use client";

import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";
import { ExternalLink } from "lucide-react";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface VideoRoomFallbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  fallbackUrl: string;
}

export default function VideoRoomFallbackDialog({
  isOpen,
  onClose,
  fallbackUrl,
}: VideoRoomFallbackDialogProps) {
  const { t } = useTranslation(["common", "classroom"]);

  const handleJoinFallback = () => {
    // Open fallback URL in a new tab
    window.open(fallbackUrl, "_blank", "noopener,noreferrer");
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-foreground/80 text-accent-foreground w-fit max-w-lg overflow-hidden rounded-2xl border-none p-0 shadow-2xl backdrop-blur-xl max-sm:max-w-[90vw] [&_>_button]:z-50!">
        <motion.div
          className="relative"
          initial={false}
          transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Background decorative elements */}
          <div className="absolute inset-0 opacity-50">
            <div className="absolute -top-20 -left-20 size-40 rounded-full bg-gradient-to-br from-orange-600 to-red-800" />
            <div className="absolute -right-18 -bottom-18 size-30 rounded-full bg-gradient-to-tr from-orange-600 to-red-600" />
          </div>

          <div className="relative z-10 p-6 max-sm:p-4">
            <motion.div
              animate={{ opacity: 1 }}
              className="text-center"
              initial={{ opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              {/* Header with inline icon */}
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                className="mb-4"
                initial={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
              >
                <h2 className="text-accent-foreground flex items-center justify-center gap-3 text-xl font-bold max-sm:text-lg">
                  <Icon
                    className="text-orange-500 flex-shrink-0"
                    height="1.5rem"
                    icon="tabler:video-off"
                    width="1.5rem"
                  />
                  {t("classroom:fallbackDialog.title", {
                    defaultValue: "Oops! Something went wrong"
                  })}
                </h2>
              </motion.div>

              {/* Simple description */}
              <motion.div
                animate={{ opacity: 1 }}
                className="mb-6"
                initial={{ opacity: 0 }}
                transition={{ duration: 0.25, delay: 0.2, ease: "easeOut" }}
              >
                <p className="text-accent-foreground/70 text-base leading-relaxed max-sm:text-sm">
                  {t("classroom:fallbackDialog.description", {
                    defaultValue: "We couldn't create a video room for your lesson. Don't worry - you can still join using the fallback link below to start your lesson."
                  })}
                </p>
              </motion.div>

              {/* Action Buttons in a row */}
              <motion.div
                animate={{ opacity: 1 }}
                className="flex gap-3 max-sm:flex-col"
                initial={{ opacity: 0 }}
                transition={{ duration: 0.25, delay: 0.3, ease: "easeOut" }}
              >
                <Button
                  className="bg-muted/80 hover:bg-muted text-muted-foreground hover:text-accent-foreground flex-1 rounded-xl"
                  variant="ghost"
                  onClick={handleClose}
                >
                  {t("classroom:fallbackDialog.cancelButton", {
                    defaultValue: "Return to lessons"
                  })}
                </Button>
                <Button
                  className="flex-1 gap-2 rounded-xl bg-orange-500 text-white hover:bg-orange-600"
                  onClick={handleJoinFallback}
                >
                  <ExternalLink className="h-4 w-4" />
                  {t("classroom:fallbackDialog.joinButton", {
                    defaultValue: "Join with fallback link"
                  })}
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
